/**
 * The Service Builder's coaching, INLINE — the transplant of ServiceBuilderInsightPanel's guidance
 * into the builder itself (owner direction, 2026-08-25), each piece where and when it applies:
 *
 *   ServiceBuilderIntro — how the builder works, FIRST on the page (the narrative reads top to
 *                         bottom, so orientation comes before the financial-position choice). The
 *                         category rates are tiles, not bold-soup prose (owner: the paragraph
 *                         version "looks messy"); couple tips one line; the long notes behind
 *                         taps. Folds itself once services are being selected.
 *   OverBudgetHelp      — the ways to fix an exceeded budget. Lives in the RIGHT-HAND Service
 *                         Budget panel beside the red remaining figure it explains: the owner
 *                         found the left-column placement scrolled out of view exactly when the
 *                         user was down in the service lists causing the problem.
 *   ContributionCashflowNote — what the contribution leaves of yearly cashflow, beside the total.
 *
 * All wording is transposed from the panel (vetted); all figures come from the builder's own
 * state — the same values its budget bars display.
 */
import React, { useEffect, useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { formatCurrency } from '../../utils/index.ts';
import { JargonTerm } from '../../components/narrative/index.tsx';
import { PlainWordsCard } from '../../components/narrative/PlainWords.tsx';

const SERVICE_BLOCKS_NOTE =
    'Service quantities are whole blocks (hours, trips, meals) — providers typically have minimum blocks, and partial blocks (like half an hour) cannot be booked. If you need less than a full block, discuss options with the provider; exact minimums vary by provider.';

const FINANCIAL_POSITION_NOTE =
    'The "Select Financial Position" dropdown re-costs this whole plan under a different financial position: Baseline uses today’s position with no strategies, or pick any strategy saved in Optimise Your Plan to see how it changes the contribution rates — and so what every service here costs you. Switching lets you compare affordability under each approach.';

export function ServiceBuilderIntro({
    independenceRate,
    everydayRate,
    isPartnered,
    clientName,
    partnerName,
    clientBudget,
    partnerBudget,
    hasLinkedAgedCareStrategy,
    hasSelections,
}: {
    independenceRate: number;
    everydayRate: number;
    isPartnered: boolean;
    clientName: string;
    partnerName: string;
    clientBudget: number;
    partnerBudget: number;
    hasLinkedAgedCareStrategy: boolean;
    hasSelections: boolean;
}) {
    // Open until the user is clearly under way, then folds to its heading — reopenable any time.
    const [open, setOpen] = useState(!hasSelections);
    const [userToggled, setUserToggled] = useState(false);
    useEffect(() => {
        if (hasSelections && !userToggled) setOpen(false);
    }, [hasSelections, userToggled]);

    const pct = (v: number) => `${(v * 100).toFixed(1)}%`;

    const rateTiles: Array<{ label: string; rate: string; sub: string }> = [
        { label: 'Clinical care', rate: '0%', sub: 'always government-funded' },
        { label: 'Independence', rate: pct(independenceRate), sub: 'your share of each hour' },
        { label: 'Everyday living', rate: pct(everydayRate), sub: 'your share of each hour' },
    ];

    return (
        <PlainWordsCard eyebrow="How the Service Builder works" className="">
            {open ? (
                <>
                    <p className="text-sm leading-relaxed text-slate-700">
                        Pick the services needed and how often — the government package funds them, and the household
                        pays a contribution on each category:
                    </p>
                    <div className="mt-2 grid grid-cols-3 gap-2">
                        {rateTiles.map((tile) => (
                            <div key={tile.label} className="rounded-lg border border-slate-200 bg-white px-2.5 py-2 shadow-sm">
                                <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{tile.label}</p>
                                <p className="text-base font-bold tabular-nums text-slate-900">{tile.rate}</p>
                                <p className="text-[11px] leading-snug text-slate-500">{tile.sub}</p>
                            </div>
                        ))}
                    </div>
                    <p className="mt-2 text-xs leading-relaxed text-slate-500">
                        The two paid rates are set by the means assessment — the strategies in Optimise Your Plan can
                        lower them.
                    </p>
                    {isPartnered && !hasLinkedAgedCareStrategy && (
                        <p className="mt-1.5 text-sm leading-relaxed text-slate-700">
                            {clientName} and {partnerName} each have their own budget ({formatCurrency(clientBudget)} and{' '}
                            {formatCurrency(partnerBudget)} a year). Shared services like cleaning or gardening can sit on
                            either — put them on whoever has more room.
                        </p>
                    )}
                    {isPartnered && hasLinkedAgedCareStrategy && (
                        <p className="mt-1.5 text-sm leading-relaxed text-slate-700">
                            With one partner in residential care under the linked plan, only{' '}
                            <span className="font-semibold text-slate-900">{partnerName}'s</span> package (
                            {formatCurrency(partnerBudget)}/yr) is available here — use their tab to allocate services for
                            the person at home.
                        </p>
                    )}
                    <p className="mt-1.5 text-xs leading-relaxed text-slate-500">
                        Worth knowing: <JargonTerm term="services come in whole blocks">{SERVICE_BLOCKS_NOTE}</JargonTerm>
                        {' · '}
                        <JargonTerm term="what the financial position dropdown does">{FINANCIAL_POSITION_NOTE}</JargonTerm>
                    </p>
                    <button
                        type="button"
                        onClick={() => { setOpen(false); setUserToggled(true); }}
                        className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-slate-800"
                    >
                        <ChevronUp size={14} /> Hide
                    </button>
                </>
            ) : (
                <button
                    type="button"
                    onClick={() => { setOpen(true); setUserToggled(true); }}
                    className="inline-flex items-center gap-1 text-sm font-medium text-slate-600 hover:text-slate-900"
                >
                    <ChevronDown size={15} /> Show the guide — categories, contribution rates and couple budgets
                </button>
            )}
        </PlainWordsCard>
    );
}

/** The ways out of an exceeded budget — rendered by the caller only while actually over. */
export function OverBudgetHelp({
    overBy,
    personName,
    isPartnered,
    otherName,
    otherHasRoom,
}: {
    overBy: number;
    /** Whose budget is exceeded (couples run one budget per person). */
    personName?: string;
    isPartnered: boolean;
    otherName: string;
    otherHasRoom: boolean;
}) {
    return (
        <div className="rounded-lg border border-slate-200/70 border-l-4 border-l-red-500 bg-white p-3 text-sm leading-relaxed text-red-800 shadow-sm">
            <p className="font-bold text-red-900 mb-1">
                {personName ? `${personName}'s plan` : 'The plan'} is {formatCurrency(overBy)} above the package
            </p>
            <p className="text-xs leading-relaxed">
                That's allowed — services above the package can be kept and{' '}
                <span className="font-semibold">bought privately at full price</span> (no subsidy, no contribution
                rate on that part), and the cashflow figures below already include it. To bring the plan back inside
                the package instead: reduce the frequency or quantity of what's selected; drop the nice-to-haves and
                keep the essentials; lean on <span className="font-semibold">clinical care</span> where possible — it
                carries no contribution
                {isPartnered && otherHasRoom && <>; or move shared services onto {otherName}'s budget, which has room</>}
                . Either way, confirm pricing and arrangements with the provider.
            </p>
        </div>
    );
}

/** What the contribution leaves of the year's cashflow — one line beside the total it refers to. */
export function ContributionCashflowNote({
    annualContribution,
    currentCashflow,
}: {
    annualContribution: number;
    currentCashflow: number;
}) {
    if (!(annualContribution > 0)) return null;
    const remaining = currentCashflow - annualContribution;
    return (
        <p className="mt-1 text-xs text-slate-500">
            Paid from cashflow — leaves{' '}
            <span className={`font-semibold ${remaining >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                {formatCurrency(remaining)}
            </span>{' '}
            a year after this contribution.
        </p>
    );
}
